import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '-';
  if (typeof d === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
  if (typeof d === 'string' && d.startsWith('/Date(')) {
    const ms = parseInt(d.replace(/\/Date\((\d+)[^)]*\)\//, '$1'));
    if (!isNaN(ms)) { const dt = new Date(ms); return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`; }
  }
  const p = new Date(d);
  if (isNaN(p)) return '-';
  return `${String(p.getDate()).padStart(2, '0')}/${String(p.getMonth() + 1).padStart(2, '0')}/${p.getFullYear()}`;
};
const fmt2Date = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const PRIMARY = '#1c2266';
const PRIMARY_MID = '#3f51b5';
const PRIMARY_LT = '#e8eaf6';
const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const STATUS_COLORS = {
  'PAID': { bg: '#dcfce7', color: '#166534' },
  'PARTIAL': { bg: '#fef9c3', color: '#854d0e' },
  'PENDING': { bg: '#fee2e2', color: '#991b1b' },
};

const SalesManCommissionExceeded = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate] = useState(fmt2Date(today));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetched, setFetched] = useState(false);
  const [executives, setExecutives] = useState([]);
  const [salesManId, setSalesManId] = useState('');

  useEffect(() => {
    if (companyId && token) {
      fetchData('SalesExecutiveApi', `/${companyId}`, token)
        .then(res => setExecutives(res?.response_data || res || []))
        .catch(() => setExecutives([]));
    }
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const smParam = salesManId || '0';
      const res = await fetchData('SaleReportApi', `/SalesmanCommissionExceededDetails/${companyId}/${branchId}/${smParam}/${fromDate}/${toDate}`, token);
      const rows = res?.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch salesman commission exceeded report');
    } finally {
      setLoading(false);
    }
  };

  // Group by salesman for summary footer
  const smTotals = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const sm = r.SalesManName || '(Unknown)';
      if (!map[sm]) map[sm] = { grandTotal: 0, paidAmount: 0, commissionAmount: 0 };
      map[sm].grandTotal += parseFloat(r.GrandTotal) || 0;
      map[sm].paidAmount += parseFloat(r.PaidAmount) || 0;
      map[sm].commissionAmount += parseFloat(r.CommissionAmount) || 0;
    });
    return map;
  }, [data]);

  const grandTotals = useMemo(() => data.reduce((acc, r) => ({
    grandTotal: acc.grandTotal + (parseFloat(r.GrandTotal) || 0),
    paidAmount: acc.paidAmount + (parseFloat(r.PaidAmount) || 0),
    commissionAmount: acc.commissionAmount + (parseFloat(r.CommissionAmount) || 0),
    taxableAmount: acc.taxableAmount + (parseFloat(r.TaxableAmount) || 0),
    quantity: acc.quantity + (parseFloat(r.Quantity) || 0),
  }), { grandTotal: 0, paidAmount: 0, commissionAmount: 0, taxableAmount: 0, quantity: 0 }), [data]);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'SalesManCommissionExceeded', 0);
    if (!data.length) return;
    const headers = ['#', 'Bill Date', 'Bill No', 'Customer', 'Item', 'Category',
      'Qty', 'Taxable Amt', 'Comm Mode', 'Comm Rate', 'Comm Value',
      'Grand Total', 'Paid Amt', 'Due Days', 'Age Days', 'Payment Date', 'Status', 'Commission Amt'];
    const rows = data.map((r, i) => [
      i + 1, r.SaleId, fmtDate(r.BillDate), r.BillNo, r.CustomerName,
      r.ItemName, r.CategoryName,
      r.Quantity, r.TaxableAmount, r.CommissionMode, r.CommissionRate, r.CommissionValue,
      r.GrandTotal, r.PaidAmount, r.DueDays, r.AgeDays, fmtDate(r.PaymentDate), r.Status, r.CommissionAmount,
    ]);

      await exportStyledExcel({
          reportTitle: 'SALESMAN COMMISSION EXCEEDED REPORT',
          sheetName: 'Commission Exceeded',
          fileName: `SalesManCommissionExceeded_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: headers,
          rows,
          totalsRow: ['', '', '', '', '', '', 'Totals',
        fmtN(grandTotals.quantity), fmtN(grandTotals.taxableAmount), '', '', '',
        fmtN(grandTotals.grandTotal), fmtN(grandTotals.paidAmount), '', '', '', '',
        fmtN(grandTotals.commissionAmount)],
      });
  };

  const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #c8cfe8', color: '#fff', background: PRIMARY_MID };
  const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading commission exceeded report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #2d3494 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Salesman Commission Exceeded</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <SearchableDropdown
            value={salesManId}
            onChange={v => setSalesManId(v)}
            options={[{ value: '', label: '— All Salesman —' }, ...executives.map(e => ({ value: String(e.Id), label: e.Name }))]}
            placeholder="— All Salesman —"
            minWidth={160}
            primaryColor={PRIMARY}
          />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Meta info */}
        {fetched && (
          <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
            {fromDate} to {toDate} &nbsp;|&nbsp; {data.length} records &nbsp;|&nbsp; {Object.keys(smTotals).length} salesmen
          </div>
        )}

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 140px)' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 400, width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr>
                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                {/* <th style={{ ...thBase, textAlign: 'center' }}>Sale ID</th> */}
                <th style={{ ...thBase, textAlign: 'center' }}>Bill Date</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Bill No</th>
                <th style={{ ...thBase, textAlign: 'left', minWidth: 160 }}>Customer</th>
                {/* <th style={{ ...thBase, textAlign: 'left', minWidth: 130 }}>Salesman</th> */}
                <th style={{ ...thBase, textAlign: 'left', minWidth: 150 }}>Item</th>
                <th style={{ ...thBase, textAlign: 'left' }}>Category</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Qty</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Taxable Amt</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Mode</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Rate</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Grand Total</th>
                <th style={{ ...thBase, textAlign: 'right' }}>Paid Amt</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Due Days</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Age Days</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Payment Date</th>
                <th style={{ ...thBase, textAlign: 'center' }}>Status</th>
                <th style={{ ...thBase, textAlign: 'right', background: '#0f4c81' }}>Commission</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={18} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click Refresh'}
                  </td>
                </tr>
              ) : (
                data.map((r, i) => {
                  const statusStyle = STATUS_COLORS[(r.Status || '').toUpperCase()] || { bg: '#f1f5f9', color: '#475569' };
                  const rowBg = i % 2 === 0 ? '#fff' : '#e8eaf6';
                  return (
                    <tr key={`${r.SaleId}-${r.ItemId}-${i}`} style={{ background: rowBg }}>
                      <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{r.RN || i + 1}</td>
                      {/* <td style={{ ...tdBase, textAlign: 'center', fontWeight: 600, color: PRIMARY }}>{r.SaleId}</td> */}
                      <td style={{ ...tdBase, textAlign: 'center' }}>{fmtDate(r.BillDate)}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>{(r.BillNo)}</td>
                      <td style={{ ...tdBase, fontWeight: 500 }}>{r.CustomerName}</td>
                      {/* <td style={{ ...tdBase, color: '#374151' }}>{r.SalesManName}</td> */}
                      <td style={{ ...tdBase }}>{r.ItemName}</td>
                      <td style={{ ...tdBase, color: '#6b7280' }}>{r.CategoryName}</td>
                      <td style={{ ...tdBase, textAlign: 'right' }}>{fmtN(r.Quantity)}</td>
                      <td style={{ ...tdBase, textAlign: 'right' }}>{fmtN(r.TaxableAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: r.CommissionMode === 'QTY' ? '#dbeafe' : '#fce7f3', color: r.CommissionMode === 'QTY' ? '#1d4ed8' : '#9d174d' }}>
                          {r.CommissionMode || '-'}
                        </span>
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right' }}>{fmtN(r.CommissionRate)}</td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600 }}>{fmtN(r.GrandTotal)}</td>
                      <td style={{ ...tdBase, textAlign: 'right', color: '#059669' }}>{fmtN(r.PaidAmount)}</td>
                      <td style={{ ...tdBase, textAlign: 'center', color: r.DueDays > 30 ? '#dc2626' : '#374151' }}>{r.DueDays ?? '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'center', color: r.AgeDays > 60 ? '#dc2626' : r.AgeDays > 30 ? '#d97706' : '#374151', fontWeight: r.AgeDays > 30 ? 600 : 400 }}>{r.AgeDays ?? '-'}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>{fmtDate(r.PaymentDate)}</td>
                      <td style={{ ...tdBase, textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: statusStyle.bg, color: statusStyle.color }}>
                          {r.Status || '-'}
                        </span>
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669', background: '#f0fdf4' }}>{fmtN(r.CommissionAmount)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {data.length > 0 && (
              <tfoot>
                <tr style={{ background: '#dbeafe', fontWeight: 700 }}>
                  <td colSpan={6} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(grandTotals.quantity)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(grandTotals.taxableAmount)}</td>
                  <td colSpan={2} style={{ ...tdBase }} />
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(grandTotals.grandTotal)}</td>
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtN(grandTotals.paidAmount)}</td>
                  <td colSpan={4} style={{ ...tdBase }} />
                  <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669', background: '#dcfce7' }}>{fmtN(grandTotals.commissionAmount)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default SalesManCommissionExceeded;



